import React, { createContext, useState, useEffect, useRef } from "react";
import apiService from "./apiServices";

const NotificationContext = createContext();

const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const failCountRef = useRef(0);       // consecutive failure counter
  const intervalRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    const fetchNotifications = async () => {
      try {
        const response = await apiService.get('/notifications');
        if (!isMountedRef.current) return;

        if (response.data && Array.isArray(response.data)) {
          setNotifications(response.data);
        } else if (response.data && Array.isArray(response.data.notifications)) {
          setNotifications(response.data.notifications);
        }

        // Success — reset failure counter, resume normal 30s polling
        if (failCountRef.current > 0) {
          failCountRef.current = 0;
          schedulePoll(30000);
        }
      } catch (error) {
        if (!isMountedRef.current) return;

        failCountRef.current += 1;

        // Exponential backoff: 30s → 60s → 120s → 300s (5 min max), then stop console spam
        const backoffMs = Math.min(30000 * Math.pow(2, failCountRef.current - 1), 300000);

        if (failCountRef.current === 1) {
          // Only log the first failure — not every retry
          console.warn("[Notifications] Offline or API unreachable. Backing off.");
        }

        schedulePoll(backoffMs);
      }
    };

    const schedulePoll = (delay) => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
      if (!isMountedRef.current) return;
      intervalRef.current = setTimeout(fetchNotifications, delay);
    };

    // Initial fetch
    fetchNotifications();

    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, []);

  return (
    <NotificationContext.Provider value={{ notifications, setNotifications }}>
      {children}
    </NotificationContext.Provider>
  );
};

export { NotificationContext, NotificationProvider };
